/**
 * BottomNavBar - A bottom navigation bar with tabs for Battle and Characters views
 */

export type NavTab = 'battle' | 'characters';

export interface BottomNavBarOptions {
  onTabChange?: (tab: NavTab) => void;
}

export class BottomNavBar {
  private container: HTMLElement;
  private element: HTMLElement | null = null;
  private activeTab: NavTab = 'battle';
  private onTabChange: (tab: NavTab) => void;

  constructor(container: HTMLElement, options: BottomNavBarOptions = {}) {
    this.container = container;
    this.onTabChange = options.onTabChange || (() => {});
    this.createNavBar();
  }

  private createNavBar(): void {
    this.element = document.createElement('div');
    this.element.id = 'bottom-nav-bar';
    this.element.innerHTML = `
      <style>
        #bottom-nav-bar {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 4px;
          padding: 8px 12px;
          padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
          background: linear-gradient(to right, #449bdd, #154088);
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
          z-index: 9999;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .nav-tab {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 8px 24px;
          border: none;
          background: transparent;
          border-radius: 12px;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s;
          color: rgba(255, 255, 255, 0.6);
        }

        .nav-tab:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .nav-tab:active {
          transform: scale(0.95);
        }

        .nav-tab--active {
          background: rgba(96, 165, 250, 0.2);
          color: #60a5fa;
        }

        .nav-tab--active:hover {
          background: rgba(96, 165, 250, 0.25);
        }

        .nav-tab-icon {
          width: 28px;
          height: 28px;
          object-fit: contain;
          filter: grayscale(100%) brightness(0.7);
          transition: filter 0.2s;
        }

        .nav-tab--active .nav-tab-icon {
          filter: none;
        }

        .nav-tab-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      </style>

      <button class="nav-tab nav-tab--active" data-tab="battle">
        <img class="nav-tab-icon" src="/sword.png" alt="Battle">
        <span class="nav-tab-label">Battle</span>
      </button>
      <button class="nav-tab" data-tab="characters">
        <img class="nav-tab-icon" src="/cards.png" alt="Characters">
        <span class="nav-tab-label">Characters</span>
      </button>
    `;

    this.container.appendChild(this.element);
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    const tabs = this.element?.querySelectorAll('.nav-tab');
    tabs?.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = tab.getAttribute('data-tab') as NavTab;
        if (tabName && tabName !== this.activeTab) {
          this.setActiveTab(tabName);
        }
      });
    });
  }

  setActiveTab(tab: NavTab): void {
    this.activeTab = tab;
    this.updateUI();
    this.onTabChange(tab);
  }

  getActiveTab(): NavTab {
    return this.activeTab;
  }

  private updateUI(): void {
    const tabs = this.element?.querySelectorAll('.nav-tab');
    tabs?.forEach(tabEl => {
      const tabName = tabEl.getAttribute('data-tab');
      if (tabName === this.activeTab) {
        tabEl.classList.add('nav-tab--active');
      } else {
        tabEl.classList.remove('nav-tab--active');
      }
    });
  }

  show(): void {
    if (this.element) {
      this.element.style.display = 'flex';
    }
  }

  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  dispose(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
